import { FC } from "react";
import { useSelector } from "react-redux";

import { DesignToken, Modal, ModalPortal } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import Text from "components/layout/Text";
import { KeyCodeEnum } from "constants/keyCode";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isMac } from "helpers/isMac";
import { selectIsViewer } from "store/entities/agents";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { KeyModifier, Shortcuts } from "./Shortcuts";
import ShortcutsItem from "./ShortcutsItem";
import { getGlobalShortcutsList, getTicketingActionsShortcutsList } from "./shortcutsList";

interface IProps {
  isOpen: boolean;
  toggleOpen: () => void;
}

const ShortcutsModal: FC<IProps> = ({ isOpen, toggleOpen }) => {
  const userOnMac = isMac(navigator.userAgent);
  const isViewer = useSelector(selectIsViewer);
  const ticketingActionsShortcuts = getTicketingActionsShortcutsList(userOnMac, isViewer);
  const globalShortcuts = getGlobalShortcutsList();

  const shortcutsConfig = {
    [KeyCodeEnum.Slash]: {
      additionalKeysRequired: [KeyModifier.Shift],
      disableDefaultBehavior: true,

      eventHandler: (): void => {
        toggleOpen();
        trackEngagement("Tickets: Shortcut used", EventPlace.Tickets, {
          shortcut: "showShortcutList",
        });
      },
    },
  };

  return (
    <Shortcuts config={shortcutsConfig}>
      {isOpen && (
        <ModalPortal zIndex={StackingContextLevel.OverChatWidget}>
          <Modal
            data-testid="shortcuts-modal"
            closeOnEscPress
            onClose={toggleOpen}
            heading="Keyboard shortcuts"
            fullSpaceContent={false}
          >
            <Flex column gap="16px" minWidth="650px" maxHeight="440px" overflowY="scroll">
              <Text fontWeight="600" color={withVar(DesignToken.ContentBasicPrimary)}>
                Ticketing actions
              </Text>
              {ticketingActionsShortcuts.map(props => (
                <ShortcutsItem key={props.label} {...props} />
              ))}
              <Text fontWeight="600" color={withVar(DesignToken.ContentBasicPrimary)}>
                Global
              </Text>
              {globalShortcuts.map(props => (
                <ShortcutsItem key={props.label} {...props} />
              ))}
            </Flex>
          </Modal>
        </ModalPortal>
      )}
    </Shortcuts>
  );
};

export default ShortcutsModal;
